import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  TaskTemplate,
  CreateTemplateRequest,
  UpdateTemplateRequest,
  CreateTemplateSchema,
  UpdateTemplateSchema,
  useCreateTemplate,
  useUpdateTemplate,
} from "@/lib/api/checklist/templates";
import { toast } from "@/components/ui/use-toast";

interface CreateEditTemplateModalProps {
  open: boolean;
  onClose: () => void;
  template?: TaskTemplate;
}

export function CreateEditTemplateModal({
  open,
  onClose,
  template,
}: CreateEditTemplateModalProps) {
  const isEdit = !!template;
  const createTemplate = useCreateTemplate();
  const updateTemplate = useUpdateTemplate(template?.id || "");

  const form = useForm<CreateTemplateRequest | UpdateTemplateRequest>({
    resolver: zodResolver(isEdit ? UpdateTemplateSchema : CreateTemplateSchema),
    defaultValues: isEdit
      ? {
          name: template.name,
          description: template.description || "",
          active: template.active,
        }
      : {
          name: "",
          description: "",
        },
  });

  useEffect(() => {
    if (open && template) {
      form.reset({
        name: template.name,
        description: template.description || "",
        active: template.active,
      });
    } else if (open && !template) {
      form.reset({
        name: "",
        description: "",
      });
    }
  }, [open, template, form]);

  const handleSubmit = async (data: CreateTemplateRequest | UpdateTemplateRequest) => {
    if (createTemplate.isLoading || updateTemplate.isLoading) return;

    try {
      if (isEdit) {
        const result = await updateTemplate.mutateAsync(data as UpdateTemplateRequest);
        if (!result.success) { throw result; }
        toast({
          title: "Success",
          description: "Template updated successfully",
        });
      } else {
        // For creation, we'll create an empty template first
        // Tasks will be added separately through the detail view
        const createData = data as CreateTemplateRequest;
        const result = await createTemplate.mutateAsync(createData);
        if (!result.success) { throw result; }

        toast({
          title: "Success",
          description: "Template created successfully",
        });
      }

      onClose();
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: `Failed to ${isEdit ? "update" : "create"} template`,
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {isEdit ? "Edit Template" : "Create New Template"}
          </DialogTitle>
          <DialogDescription>
            {isEdit
              ? "Update the template information"
              : "Create a new task template. You can add tasks after creating the template."}
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Template Name</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder="e.g., Customer Onboarding"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Textarea
                      {...field}
                      placeholder="Describe what this template is for..."
                      rows={3}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {isEdit && (
              <FormField
                control={form.control}
                name="active"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3">
                    <div className="space-y-0.5">
                      <FormLabel>Active</FormLabel>
                      <FormDescription>
                        Inactive templates cannot be triggered
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            )}

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={createTemplate.isLoading || updateTemplate.isLoading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={createTemplate.isLoading || updateTemplate.isLoading}
              >
                {createTemplate.isLoading || updateTemplate.isLoading
                  ? "Saving..."
                  : isEdit
                  ? "Update Template"
                  : "Create Template"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
